import { searchPeriodOptions } from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import { FilterIcon } from '@airship-platform/jam-web/icons';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { Link } from 'react-router';
import { Fragment } from 'react/jsx-runtime';
import { z } from 'zod';
import { ProposalStatusSchema } from '../../../../data-access/appointmentHistory';

export const StatusSchema = z.enum([
  'SCHEDULED',
  'DISPATCHED',
  'IN-PROGRESS',
  'COMPLETED',
  'ON-HOLD',
  'CANCELLED',
]);

const sortingOptions = [
  { value: 'DATE_DESC', label: 'Date: newest' },
  { value: 'DATE_ASC', label: 'Date: oldest' },
] as const;

export const AppointmentHistoryFormSchema = z.object({
  searchTerm: z.string(),
  sorting: z.enum(['DATE_ASC', 'DATE_DESC']),
  searchPeriod: z.string(),
  status: StatusSchema.array(),
  proposalStatus: ProposalStatusSchema.array(),
  includeNonAirshipJobs: z.boolean(),
  assignedUserIds: z.string().array(),
});

export type AppointmentHistoryFormInputs = z.infer<
  typeof AppointmentHistoryFormSchema
>;

interface AppointmentSearchProps {
  isLoading: boolean;
}

const countActiveFilters = (
  values: Pick<
    AppointmentHistoryFormInputs,
    'status' | 'proposalStatus' | 'assignedUserIds'
  >
): number =>
  values.status.length +
  values.proposalStatus.length +
  values.assignedUserIds.length;

const AppointmentSearchForm: React.FC<AppointmentSearchProps> = ({
  isLoading,
}) => {
  const { control, watch } = useFormContext<AppointmentHistoryFormInputs>();
  const [status, proposalStatus, assignedUserIds] = watch([
    'status',
    'proposalStatus',
    'assignedUserIds',
  ]);
  const activeFilterCount = countActiveFilters({
    status,
    proposalStatus,
    assignedUserIds,
  });

  return (
    <Fragment>
      <FlexBox display={'flex'} gap={2} flexDirection={['column', 'row']}>
        <Controller
          name="searchTerm"
          control={control}
          render={({ field }) => (
            <TextField
              label="Search"
              disabled={isLoading}
              type="search"
              {...field}
            />
          )}
        />

        <Controller
          name="searchPeriod"
          control={control}
          render={({ field }) => (
            <TextField
              label="Search Period"
              select
              disabled={isLoading}
              {...field}
            >
              {searchPeriodOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <FlexBox>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            invisible={activeFilterCount === 0}
          >
            <Button
              disabled={isLoading}
              loading={isLoading}
              type="button"
              variant="outlined"
              color="gray"
              component={Link}
              to={'filters'}
              startIcon={<FilterIcon />}
            >
              Filter
            </Button>
          </Badge>
        </FlexBox>
      </FlexBox>
      <FlexBox display={'flex'} gap={2} flexDirection={['column', 'row']}>
        <Controller
          name="sorting"
          control={control}
          render={({ field }) => (
            <RadioGroup {...field} row>
              {sortingOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio value={option.value} size="small" />}
                  label={
                    <Typography variant="caption">{option.label}</Typography>
                  }
                />
              ))}
            </RadioGroup>
          )}
        />
      </FlexBox>
    </Fragment>
  );
};

export default AppointmentSearchForm;
